import { connect } from 'react-redux';
import i18next from 'i18next';
import moment from 'moment-timezone';
import React, { useEffect, useState } from 'react';

// ACTIONS
import {
  closeGenericModal,
  openGenericModal,
  openSmallModal,
  refreshGenericModal,
} from '@actions/modal';
import { showConfirmationMessage, showErrorMessage } from '@actions/messageconfirmation';

// COMMONS
import { ANALYTICS_TYPE } from '@commons/constants/analyticsType';
import { Button, NestedList } from '@commons/utils/styledLibraryComponents';
import { DATE_DISPLAY_FORMATS } from '@commons/DatePickers/constants';
import { getAnalyticsFoodCostByStoreExportModalConfig } from './utils/exportUtils';
import { getClientStoreNameTranslation } from '@commons/utils/translations';
import { getFilteredStoresWithSelectedGroups } from '@commons/ExportModalWithStoresAndPeriod/utils';
import { getUniqueGroupsOfStores } from '@commons/utils/groups';
import { getUserTimezone } from '@commons/utils/date';
import { PeriodDatePicker } from '@commons/DatePickers/PeriodDatePicker';
import { usePeriodDatePickerState } from '@hooks/usePeriodDatePickerState';
import DeepsightFiltersButton from '@commons/DeepsightAnalyticsHeaders/components/DeepsightFiltersButton';
import EmptyState from '@commons/EmptyState';
import NavigationBreadCrumb from '@commons/Breadcrumb/NavigationBreadCrumb';
import StoresWithActiveProperty from '@commons/DropdownItems/StoresWithActiveProperty';

// SELECTORS
import { getClientInfo } from '@selectors/client';
import { getSalesPointStores } from '@selectors/stores';

import { fetchInventoryDateOfStoreByDates } from '../OrderAnalyticsCategory/utils/fetch';
import OrderAnalyticsProgressExportModal from '../components/OrderAnalyticsProgressExportModal';

import {
  Container,
  ContentContainer,
  Filter,
  FilterContainer,
  HeaderContainer,
  NestedListContainer,
} from './styledComponents';
import { getCustomFilterWithNonSalesConsumption } from '@commons/utils/filtersFetches';
import fetchUtils from './utils/fetch';
import getColumnsTable from './utils/columns';

export const OrdersAnalyticsStore = (props) => {
  const {
    user,
    client: { hasMultipleBrands, storeName, defaultTimezone },
    match,
    stores,
    showMessage,
    showErrorMessage,
    currency,
    clientBrands,
    clientRetailers,
    openGenericModal,
    refreshGenericModal,
    closeGenericModal,
  } = props;

  const userTimezone = getUserTimezone();

  /**
   * FILTERS STATES
   */
  const [locations, setLocations] = useState(null);
  const [selectedLocations, setSelectedLocations] = useState([]);

  const [selectedBrands, setSelectedBrands] = useState([]);

  const [selectedRetailers, setSelectedRetailers] = useState([]);

  const [groups, setGroups] = useState([]);
  const [selectedGroups, setSelectedGroups] = useState([]);

  const [formattedStores] = useState(
    stores.map((store) => ({
      ...store,
      renderValue: () => StoresWithActiveProperty(store),
    })),
  );
  const [selectedStores, setSelectedStores] = useState([]);

  // Export modal states
  const [exportModalStores, setExportModalStores] = useState(stores);
  const [exportSelectedStores, setExportSelectedStores] = useState(null);
  const [exportGroups, setExportGroups] = useState([]);
  const [exportSelectedGroups, setExportSelectedGroups] = useState([]);
  const [exportPeriod, setExportPeriod] = useState(null);

  const yesterday = moment.tz(userTimezone).subtract(1, 'days').endOf('day');

  const periodPickerState = usePeriodDatePickerState(
    moment.tz(userTimezone).subtract(1, 'month').startOf('day'),
    yesterday,
    userTimezone,
  );

  /**
   * ANALYTICS STATES
   */
  const [analytics, setAnalytics] = useState(null);
  const [activeData, setActiveData] = useState([]);
  const [totalRow, setTotalRow] = useState([]);

  const [focusedMonth, setFocusedMonth] = useState(
    moment
      .tz(userTimezone)
      .subtract(1, 'month')
      .startOf('month')
      .format(DATE_DISPLAY_FORMATS.DASHED_YEAR_MONTH_DAY),
  );
  /*
    datesWithInventory is an object of the shape
    { startDate: [], endDate: [] }
    where each array contains the dates on which an inventory exists, taking into account the stock convention
    see fetchInventoryDateOfStoreByDates for more information
  */
  const [datesWithInventory, setDatesWithInventory] = useState({});

  const [advancedFilters, setAdvancedFilters] = useState(null);
  const [applyFilters, setApplyFilters] = useState(true);
  const [filtersAnalyticsOrders, setFiltersAnalyticsOrders] = useState(null);
  const [withNonSalesConsumption, setWithNonSalesConsumption] = useState(true);
  const customFilterWithNonSalesConsumption = getCustomFilterWithNonSalesConsumption(
    withNonSalesConsumption,
    setWithNonSalesConsumption,
  );

  const [columns, setColumns] = useState([]);

  /**
   * LOADING STATES
   */
  const [isLoading, setIsLoading] = useState(true);
  const [isRetrievingAnalytics, setIsRetrievingAnalytics] = useState(true);

  const [renderEmptyState, setRenderEmptyState] = useState(false);

  useEffect(() => {
    if (!stores || !stores.length) {
      return;
    }

    setColumns(getColumnsTable(currency, periodPickerState.startDate, periodPickerState.endDate));

    // Set default selected stores
    const activeStores = formattedStores.filter(({ active }) => active);

    setSelectedStores(activeStores);

    (async function loadData() {
      const storeIds = stores.map(({ id }) => id);

      const [fetchedGroups, fetchedLocations] = await Promise.all([
        fetchUtils.fetchGroupsByStoreIds(storeIds, showMessage),
        fetchUtils.fetchLocationsOfAccount(user.id, showMessage),
      ]);

      setGroups(Object.values(fetchedGroups));
      setLocations(fetchedLocations);
      setIsLoading(false);

      // Initialize export modal states
      const uniqGroups = getUniqueGroupsOfStores(stores);

      setExportGroups(uniqGroups);
      setExportSelectedGroups(uniqGroups);
    })();
  }, []);

  useEffect(() => {
    if (!exportSelectedGroups.length) {
      return;
    }

    const filteredStores = getFilteredStoresWithSelectedGroups(exportSelectedGroups, stores);

    // Initial list of stores to export
    setExportModalStores(filteredStores);

    // Selected stores to export
    setExportSelectedStores(filteredStores);
  }, [exportSelectedGroups]);

  // Handle the fetch of analytics data
  useEffect(() => {
    const storeIds = selectedStores.map((store) => store.id);

    if (
      isLoading ||
      !storeIds.length ||
      !periodPickerState.startDate ||
      !periodPickerState.endDate
    ) {
      return;
    }

    setRenderEmptyState(false);
    setIsRetrievingAnalytics(true);

    (async function loadData() {
      try {
        setAnalytics(null);

        const result = await fetchUtils.fetchAnalyticsData(
          storeIds,
          periodPickerState.startDate,
          periodPickerState.endDate,
          { withNonSalesConsumption },
        );

        setTotalRow([result.total]);
        setAnalytics(result.data);
      } catch {
        showErrorMessage(i18next.t('ORDERS.BY_CATEGORY.FETCH_DATA_FAILURE'));

        setTotalRow([{}]);
        setAnalytics([]);

        setRenderEmptyState(true);
      } finally {
        setIsRetrievingAnalytics(false);
      }
    })();
  }, [
    selectedStores,
    periodPickerState.startDate,
    periodPickerState.endDate,
    withNonSalesConsumption,
    isLoading,
  ]);

  // Handle filters
  useEffect(() => {
    if (!analytics) {
      return;
    }

    if ((!advancedFilters || !advancedFilters.length) && applyFilters) {
      setActiveData(analytics);

      return;
    }

    if (!applyFilters) {
      return;
    }

    let filteredData = analytics.filter((data) => data.id !== 'total');

    advancedFilters.forEach(({ propertyKey, doFilter, value }) => {
      filteredData = doFilter(filteredData, propertyKey, value);
    });

    const updatedTotalRow = fetchUtils.computeAndFormatTotalRow(filteredData);
    setTotalRow([updatedTotalRow]);

    setActiveData(filteredData);
  }, [analytics, advancedFilters, applyFilters]);

  // Retrieve inventory dates when only one store is selected
  useEffect(() => {
    if (selectedStores.length !== 1) {
      return;
    }

    const storeIds = selectedStores.map(({ id }) => id);

    try {
      (async function loadData() {
        const startDate = moment
          .tz(focusedMonth, userTimezone)
          .subtract(1, 'month')
          .startOf('month')
          .format(DATE_DISPLAY_FORMATS.DASHED_YEAR_MONTH_DAY);
        const endDate = moment
          .tz(focusedMonth, userTimezone)
          .add(1, 'month')
          .endOf('month')
          .format(DATE_DISPLAY_FORMATS.DASHED_YEAR_MONTH_DAY);

        const formattedDates = await fetchInventoryDateOfStoreByDates(
          storeIds,
          startDate,
          endDate,
          userTimezone,
          showMessage,
        );

        formattedDates.startDate = formattedDates.startDate.map((date) =>
          moment.tz(date, userTimezone),
        );
        formattedDates.endDate = formattedDates.endDate.map((date) =>
          moment.tz(date, userTimezone),
        );

        setDatesWithInventory(formattedDates);
      })();
    } catch {
      showErrorMessage(i18next.t('ORDERS.BY_CATEGORY.FETCH_DATA_FAILURE'));
    }
  }, [selectedStores, focusedMonth]);

  useEffect(() => {
    if (!analytics) {
      return;
    }

    setRenderEmptyState(!activeData || !activeData.length);

    if (!isRetrievingAnalytics) {
      return;
    }

    setIsRetrievingAnalytics(false);
  }, [activeData]);

  useEffect(() => {
    if (!exportSelectedStores) {
      return;
    }

    handleAnalyticsFoodCostByStoreExportModal(true);
  }, [exportModalStores, exportSelectedStores, exportPeriod]);

  const handleAnalyticsFoodCostByStoreExportModal = (shouldRefreshModal = false) => {
    const params = getAnalyticsFoodCostByStoreExportModalConfig(
      {
        maxFutureDate: yesterday,
        initialStartDate: periodPickerState.startDate,
        initialEndDate: periodPickerState.endDate,
        exportGroups,
        exportSelectedGroups,
        setExportSelectedGroups,
        exportModalStores,
        exportSelectedStores,
        setExportSelectedStores,
        setExportPeriod,
        clientStoreName: getClientStoreNameTranslation(storeName, true),
        defaultTimezone,
      },
      triggerExport,
      resetExportModal,
    );

    if (shouldRefreshModal) {
      refreshGenericModal(params);
      return;
    }

    openGenericModal(params);
  };

  const resetExportModal = () => {
    closeGenericModal();
    const uniqGroups = getUniqueGroupsOfStores(stores);

    setExportGroups(uniqGroups);
    setExportSelectedGroups(uniqGroups);
  };

  const triggerExport = async () => {
    // Close export setup modal and open export progress modal
    resetExportModal();

    if (!exportSelectedStores || !exportSelectedStores.length) {
      return;
    }

    const exportSelectedStoreIds = exportSelectedStores.map(({ id }) => id);

    try {
      const { data } = await fetchUtils.fetchAnalyticsData(
        exportSelectedStoreIds,
        exportPeriod.startDate,
        exportPeriod.endDate,
        { withNonSalesConsumption },
      );

      props.openModalExportInfo({
        component: OrderAnalyticsProgressExportModal,
        endDate: exportPeriod.endDate,
        startDate: exportPeriod.startDate,
        analytics: data,
        title: i18next.t('ORDERS.BY_CATEGORY.EXPORT_IN_PROGRESS'),
      });
    } catch {
      showMessage(i18next.t('ORDERS.BY_CATEGORY.FETCH_DATA_FAILURE'), 'error');
    }
  };

  return (
    <Container>
      <NavigationBreadCrumb featurePath={match.path} />
      <ContentContainer>
        <HeaderContainer>
          <FilterContainer>
            <Filter>
              <DeepsightFiltersButton
                advancedFilters={advancedFilters}
                analyticsType={ANALYTICS_TYPE.ORDER}
                brands={hasMultipleBrands ? clientBrands : null}
                columnsFilterList={columns.filter((column) => column.filterType === 'numeric')}
                customToggles={[customFilterWithNonSalesConsumption]}
                disableExportButton={!activeData || !activeData.length}
                exportButtonPositionLeft={true}
                filters={filtersAnalyticsOrders}
                groups={groups}
                locations={locations}
                readOnly={isRetrievingAnalytics}
                retailers={clientRetailers}
                selectedBrands={selectedBrands}
                selectedGroups={selectedGroups}
                selectedLocations={selectedLocations}
                selectedRetailers={selectedRetailers}
                selectedStores={selectedStores}
                setAdvancedFilters={setAdvancedFilters}
                setApplyFilters={setApplyFilters}
                setFilters={setFiltersAnalyticsOrders}
                setSelectedBrands={setSelectedBrands}
                setSelectedGroups={setSelectedGroups}
                setSelectedLocations={setSelectedLocations}
                setSelectedRetailers={setSelectedRetailers}
                setSelectedStores={setSelectedStores}
                setSelectedSuppliers={() => {}}
                stores={formattedStores}
                suppliers={[]}
                textFilterButton={i18next.t('GENERAL.ANALYTICS_BUTTON')}
              />
            </Filter>
            <PeriodDatePicker
              authorizedDates={
                // focusedDateInput is either 'startDate' or 'endDate'
                !!periodPickerState.focusedDateInput &&
                datesWithInventory[periodPickerState.focusedDateInput]
              }
              disabled={isRetrievingAnalytics}
              endDate={periodPickerState.endDate}
              focusedDateInput={periodPickerState.focusedDateInput}
              maxFutureDate={yesterday}
              setFocusedDateInput={periodPickerState.setFocusedDateInput}
              startDate={periodPickerState.startDate}
              timezone="Europe/Paris" // TODO - [TIMEZONES] To be replaced by IPP-5229 Admin Module
              onDatesChange={periodPickerState.handleSelectedDates}
              onNextMonthClick={(nextMonth) => setFocusedMonth(nextMonth)}
              onPrevMonthClick={(prevMonth) => setFocusedMonth(prevMonth)}
            />
          </FilterContainer>
          <Button
            color={'inpulse-default'}
            handleClick={() => handleAnalyticsFoodCostByStoreExportModal()}
            icon={'/images/inpulse/file-download-white-small.svg'}
            isDisabled={isRetrievingAnalytics}
            label={i18next.t('GENERAL.EXPORT')}
          />
        </HeaderContainer>

        <NestedListContainer>
          {renderEmptyState && (
            <div style={{ verticalAlign: 'middle', height: 'calc(100vh - 225px)' }}>
              <EmptyState
                iconHeight={'160px'}
                iconWidth={'160px'}
                label={i18next.t('ORDERS.BY_STORE.EMPTY_STATE_LABEL')}
              />
            </div>
          )}
          {!renderEmptyState && (
            <NestedList
              data={activeData}
              defaultOrderBy={'store.name'}
              defaultOrderType={'asc'}
              fixedRowsData={totalRow}
              headers={getColumnsTable(
                currency,
                periodPickerState.startDate,
                periodPickerState.endDate,
              )}
              // Necessary because isExpandable is equal to true by default
              isExpandable={false}
              isLoading={isRetrievingAnalytics}
              labelEmptyState={i18next.t('GENERAL.NO_RESULT')}
              minWidth={'978px'}
            />
          )}
        </NestedListContainer>
      </ContentContainer>
    </Container>
  );
};

const mapStateToProps = (state) => ({
  user: state.baseReducer.user,
  currency: state.baseReducer.currency,
  stores: getSalesPointStores(state.baseReducer.stores),
  client: getClientInfo(state.baseReducer.user),
  clientBrands: state.baseReducer.brands,
  clientRetailers: state.baseReducer.retailers,
});

const mapDispatchToProps = (dispatch) => ({
  showMessage: (message, type) => {
    dispatch(showConfirmationMessage(message, type));
  },
  showErrorMessage: (message) => {
    dispatch(showErrorMessage(message));
  },
  openModalExportInfo: (params) => {
    dispatch(openSmallModal(params));
  },
  openGenericModal: (params) => {
    dispatch(openGenericModal(params));
  },
  refreshGenericModal: (params) => {
    dispatch(refreshGenericModal(params));
  },
  closeGenericModal: () => {
    dispatch(closeGenericModal());
  },
});

export default connect(mapStateToProps, mapDispatchToProps)(OrdersAnalyticsStore);
